import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { ConfigProvider, Spin } from 'antd'
import { createStyles } from 'antd-style'
import { isNil } from 'lodash'

import { queryMemberAccountApi } from '@eastcoal/api/finance/accountMember/queryMemberAccount'

import { NavigateButton } from '@eastcoal/common/components/Button/NavigateButton'
import { DataError } from '@eastcoal/common/components/DataError'
import { BasicProps } from '@eastcoal/common/types/component'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'

import { FUND_PAGE_PATH } from '../../pages/Finance/Fund/constants/path'

import { AccountStatisticItem } from './components/CardItem'
import { AccountStatisticRechargeButton } from './components/RechargeButton'
import { AccountStatisticWithUnfreezeApplyButton } from './components/UnfreezeApplyButton'
import { AccountStatisticWithUnfreezeDetailButton } from './components/UnfreezeDetailButton'
import { AccountStatisticWithdrawButton } from './components/WithdrawButton'
import { AccountStatisticContext } from './Context'

const useStyles = createStyles(({ token }) => ({
  row: {
    display: 'flex',
    flexFlow: 'row nowrap',
    paddingBlockStart: token.paddingXS,
    paddingBlockEnd: token.padding,
    backgroundColor: token.colorBgLayout,
    borderRadius: token.borderRadius
  },
  col: {
    flex: 1,
    [`&+&`]: {
      borderLeft: `1px solid ${token.colorFillSecondary}`
    }
  }
}))

export const AccountStatistic = ({ className, style }: BasicProps) => {
  const { id: memberId } = useAuthStore(state => state.member)
  const result = useRequest(() => queryMemberAccountApi(), {
    manual: false,
    ready: !isNil(memberId),
    refreshDeps: [memberId]
  })

  const { data = {}, loading, error, refreshAsync } = result

  const { cx, theme, styles } = useStyles()

  return (
    <AccountStatisticContext value={result}>
      <DataError error={error} reload={refreshAsync} />
      <Spin spinning={loading}>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                contentFontSizeSM: theme.fontSizeSM,
                defaultGhostBorderColor: theme.gold,
                defaultGhostColor: theme.gold
              }
            }
          }}
        >
          <div className={cx(styles.row, className)} style={style}>
            <AccountStatisticItem
              className={styles.col}
              extra={<AccountStatisticRechargeButton />}
              label='余额'
              value={data.balance}
            />
            <AccountStatisticItem
              className={styles.col}
              label='可用余额'
              value={data.activeBalance}
              extra={
                data.hasBank ? (
                  <AccountStatisticWithdrawButton />
                ) : (
                  <div style={{ color: theme.red }}>
                    <ExclamationCircleOutlined />
                    <NavigateButton
                      size='small'
                      to={`${FUND_PAGE_PATH}#bankManager`}
                    >
                      设置提现账户
                    </NavigateButton>
                  </div>
                )
              }
            />
            <AccountStatisticItem
              className={styles.col}
              extra={<AccountStatisticWithUnfreezeApplyButton />}
              value={data.freezeAmount}
              label={
                <>
                  <span>冻结中金额</span>
                  <AccountStatisticWithUnfreezeDetailButton />
                </>
              }
            />
          </div>
        </ConfigProvider>
      </Spin>
    </AccountStatisticContext>
  )
}
